'use client';

import Footer from '@/components/Footer';
import Header from '@/components/Header';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from '@/i18n/navigation';
import { Banknote, Rocket, Shield, Video, Zap } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Suspense } from 'react';

export default function HelpPage() {
  const t = useTranslations('common');

  return (
    <div className="flex min-h-screen flex-col bg-white font-sans">
      <Suspense
        fallback={
          <div className="fixed top-0 right-0 left-0 z-50 h-[72px] border-zinc-100 border-b bg-white shadow-sm" />
        }
      >
        <Header />
      </Suspense>
      <main id="main-content" className="dark flex-1 bg-[#212120] pt-[100px] pb-12 text-white">
        <div className="container mx-auto px-4 py-10">
          <div className="mx-auto max-w-4xl space-y-8">
            {/* Header */}
            <div className="space-y-4 text-center">
              <h1 className="mb-6 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-extrabold text-5xl text-transparent tracking-tight sm:text-6xl md:text-7xl">
                {t('faq_title', { defaultMessage: 'Frequently Asked Questions' })}
              </h1>
              <p className="mx-auto max-w-2xl text-lg text-zinc-400">
                Everything you need to know about ZappingAI &amp; Sora 2. <br />
                Learn to create amazing videos.
              </p>
            </div>

            <div className="space-y-6">
              {/* Accessible heading to establish proper h1→h2→h3 hierarchy */}
              <h2 className="sr-only">FAQ Categories</h2>
              {/* Getting Started */}
              <Card className="rounded-3xl border border-zinc-600 bg-[#2f2f2f] shadow-sm">
                <CardHeader className="!flex !flex-row !items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-purple-500/10 text-purple-400">
                    <Rocket className="h-5 w-5" />
                  </div>
                  <CardTitle className="!mb-0 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-bold text-2xl text-transparent">
                    Getting Started
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="gs-1" className="border-zinc-700/50 border-t">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        What are Sora Credits?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        Sora Credits are the currency used to generate videos on our platform. Each
                        second of video generation typically costs a specific amount of credits. You
                        can purchase credits in bundles or subscribe for a monthly allowance.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="gs-2" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        What is Sora 2 and how is it different?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        Sora 2 is OpenAI&apos;s advanced AI video generation model. It produces
                        higher quality, more realistic videos with better motion and consistency
                        than previous models. We offer access to Sora 2 through our platform.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="gs-3" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        Is there a free trial?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        It&apos;s free to register on the site and you can have a look around, but
                        you will need to purchase credits to generate videos.
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>

              {/* Video Features */}
              <Card className="rounded-3xl border border-zinc-600 bg-[#2f2f2f] shadow-sm">
                <CardHeader className="!flex !flex-row !items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-500/10 text-blue-400">
                    <Video className="h-5 w-5" />
                  </div>
                  <CardTitle className="!mb-0 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-bold text-2xl text-transparent">
                    Video Features & Specs
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="vf-1" className="border-zinc-700/50 border-t">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        What video resolutions are available?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        We support multiple resolutions including 480p, 720p, and 1080p HD. Higher
                        resolutions consume more credits per second of video generated.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="vf-2" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        What video lengths can I generate?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        You can generate videos from 10 to 15 seconds. Longer videos use more
                        credits proportionally.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="vf-3" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        What aspect ratios are supported?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        We support landscape (16:9), portrait (9:16), and square (1:1) aspect
                        ratios. Portrait is perfect for TikTok, Instagram Reels, and YouTube Shorts.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="vf-4" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        Can I upload my own images to create videos?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        Yes! Image-to-video generation is available, and we recommend you use it for
                        more control over the video output. Simply upload an image and describe how
                        you want it to animate, and the AI will bring it to life.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="vf-5" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        Can I download my videos in different formats?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        Videos are currently available in MP4 format, which is universally
                        compatible with all devices and platforms. We may add more formats in the
                        future.
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>

              {/* Credits & Pricing */}
              <Card className="rounded-3xl border border-zinc-600 bg-[#2f2f2f] shadow-sm">
                <CardHeader className="!flex !flex-row !items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-500/10 text-green-400">
                    <Banknote className="h-5 w-5" />
                  </div>
                  <CardTitle className="!mb-0 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-bold text-2xl text-transparent">
                    Credits & Pricing
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="cp-1" className="border-zinc-700/50 border-t">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        How do I get more credits?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        You can purchase additional plans at any time from the{' '}
                        <Link href="/pricing" className="text-primary underline">
                          Pricing page
                        </Link>
                        . We offer various plans to suit your needs. Reload credits instantly
                        whenever you need more.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="cp-2" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        Do credits expire?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        These are subscription credits and they are valid for the billing period and
                        so your balance gets topped up each month to the amount in your plan.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="cp-3" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        What payment methods do you accept?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        We accept all major credit cards (Visa, Mastercard, American Express). All
                        payments are processed securely through Stripe.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="cp-4" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        Can I cancel my subscription anytime?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        Absolutely. You can cancel your subscription at any time from your account
                        settings. You&apos;ll retain access to your remaining credits until the end
                        of your billing period.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="cp-5" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        Do you offer refunds?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        We offer refunds on a case-by-case basis within 7 days of purchase if you
                        are unable to use your credits. Please contact our support team with your
                        refund request.
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>

              {/* Using the Platform */}
              <Card className="rounded-3xl border border-zinc-600 bg-[#2f2f2f] shadow-sm">
                <CardHeader className="!flex !flex-row !items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-500/10 text-orange-400">
                    <Zap className="h-5 w-5" />
                  </div>
                  <CardTitle className="!mb-0 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-bold text-2xl text-transparent">
                    Using the Platform
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="up-1" className="border-zinc-700/50 border-t">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        How long does it take to generate a video?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        Most videos are generated within 2-5 minutes depending on complexity,
                        resolution, and current server load. You&apos;ll receive a notification when
                        your video is ready.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="up-2" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        How do I write a good prompt for better results?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        Be specific about what you want: describe the scene, camera movements,
                        lighting, and style. Include details like &quot;cinematic,&quot; &quot;slow
                        motion,&quot; or &quot;aerial shot.&quot; The more descriptive your prompt,
                        the better the results.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="up-3" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        Can I regenerate a video if I don&apos;t like the result?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        Yes, but regenerations consume additional credits. We recommend refining
                        your prompt before regenerating. Each generation attempt uses credits
                        regardless of whether you keep the video.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="up-4" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        What if my video generation fails?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        If a generation fails due to a system error, your credits are automatically
                        refunded to your balance. If you don&apos;t see the refund immediately,
                        please refresh the page or contact support.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="up-5" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        Can I edit the video after it&apos;s generated?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        Currently, you cannot edit videos directly on our platform. However, you can
                        download your video and use any video editing software. We&apos;re working
                        on in-platform editing features.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="up-6" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        Are there any content restrictions?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        Yes. We prohibit generating violent, explicit, harmful, or copyrighted
                        content. Our content moderation system may reject prompts that violate our
                        terms of service.
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>

              {/* Account & Support */}
              <Card className="rounded-3xl border border-zinc-600 bg-[#2f2f2f] shadow-sm">
                <CardHeader className="!flex !flex-row !items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-zinc-500/10 text-zinc-400">
                    <Shield className="h-5 w-5" />
                  </div>
                  <CardTitle className="!mb-0 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-bold text-2xl text-transparent">
                    Account & Support
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="as-1" className="border-zinc-700/50 border-t">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        Can I use generated videos commercially?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        Yes! Videos generated with our paid plans come with a commercial license,
                        allowing you to use them for your business, social media, and marketing
                        campaigns.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="as-2" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        Is my data and videos private?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        Yes. Your prompts and generated videos are private by default. We do not
                        share your content with third parties or use it for training without your
                        explicit consent.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="as-3" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        Can I share my generated videos on social media?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        Yes! You can download and share your videos on any platform including
                        YouTube, TikTok, Instagram, LinkedIn, and more. There are no watermarks on
                        paid plan videos.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="as-4" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        Do you have a mobile app?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        Not currently, but our website is fully responsive and works great on mobile
                        devices. A dedicated app is on our roadmap for future development.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="as-5" className="border-zinc-700/50">
                      <AccordionTrigger className="text-left font-medium text-lg hover:no-underline">
                        How do I contact support?
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-400">
                        You can reach our support team via the{' '}
                        <Link href="/contact" className="text-primary underline">
                          Contact page
                        </Link>
                        , or through the chat widget on our website.
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>

              {/* Contact Box */}
              <Card className="rounded-3xl border border-zinc-600 bg-[#2f2f2f] text-center shadow-xl">
                <CardContent className="p-8 md:p-12">
                  <h2 className="mb-4 bg-gradient-to-r from-zinc-100 to-zinc-400 bg-clip-text font-bold text-3xl text-transparent">
                    Still have questions?
                  </h2>
                  <p className="mb-8 text-lg text-zinc-400">
                    Our support team is here to help you with any other questions you may have.
                  </p>
                  <Link
                    href="/contact"
                    className="inline-flex h-12 transform cursor-pointer items-center justify-center whitespace-nowrap rounded-full bg-white px-8 font-bold text-black shadow-md transition-all duration-300 hover:scale-105 hover:bg-zinc-200 active:scale-95"
                  >
                    Contact Support
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
